/*
 * Draft 11 in the click-through preview: Head office (companies, territories, people across brands, franchise plans),
 * the security center and a franchise's own plan, with made-up data kept in memory. The Cleaners of New Orleans plays the
 * franchise (run by Faye Owens); every other location is run by head office.
 */
import {
  ADDON_DEFAULTS,
  ADDONS,
  canGiveRole,
  cleanZips,
  monthBefore,
  TWO_STEP_ROLES,
  zipOf,
  type Action,
  type ActivityRow,
  type AddonRow,
  type BillRow,
  type CompanyLevel,
  type HqPersonRow,
  type HubSignIn,
  type OverviewRow,
  type PlanAnswer,
  type Role,
  type SecurityLogin,
} from "@steward/shared";

type Answer = Promise<{ status: number; body: unknown }>;
type Json = Record<string, unknown>;
const ok = (body: unknown): Answer => Promise.resolve({ status: 200, body });
const fail = (status: number, code: string, message: string): Answer => Promise.resolve({ status, body: { code, message } });
const NO = () => fail(403, "not_allowed", "Your role can't do that. Ask the owner if you need it.");
const minsAgo = (m: number) => new Date(Date.now() - m * 60_000).toISOString();
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
let n = 0;
const id = () => `00000000-0000-4000-8000-${String(800000000000 + (n += 1))}`;

export interface HqCo {
  id: string;
  parentId: string | null;
  name: string;
  status: "active" | "paused";
}
export interface HqUser {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  totp: boolean;
  roles: Record<string, Role>;
}
export interface HqAudit {
  companyId: string;
  id: string;
  action: string;
  userId: string | null;
  userName: string | null;
  subjectType: string | null;
  subjectId: string | null;
  ip: string | null;
  details: Json;
  createdAt: string;
}

export interface HqContext {
  c: HqCo;
  role: Role;
  me: HqUser;
  companies: HqCo[];
  users: HqUser[];
  audit: HqAudit[];
  below: (id: string) => HqCo[];
  levelOf: (c: HqCo) => CompanyLevel;
  gate: (a: Action) => boolean;
  log: (c: HqCo, action: string, details?: Json) => void;
}

// State ----------------------------------------------------------------------------------------------------------------

const zips = new Map<string, string[]>();
const hidden = new Set<string>();
const disabled = new Set<string>();
const ended = new Map<string, string>();
let review: { at: string; by: string } | null = null;
const agoIso = (min: number) => new Date(Date.now() - min * 60_000).toISOString();
/** Made-up clients signed in to the client hub. */
let hubRows: HubSignIn[] = [
  {
    key: "00000000-0000-4000-8000-00000000a101",
    clients: [
      { clientId: "00000000-0000-4000-8000-00000000c101", name: "Dana Pierce", companyName: "The Cleaners of New Orleans" },
    ],
    phoneEnd: "0142",
    lastSeenAt: agoIso(25),
    devices: [
      {
        id: "00000000-0000-4000-8000-00000000a101",
        device: "Safari on iPhone",
        ip: "73.12.40.8",
        signedInAt: agoIso(60 * 24 * 3),
        lastSeenAt: agoIso(25),
        lookOnly: false,
      },
    ],
  },
  {
    key: "00000000-0000-4000-8000-00000000a102",
    clients: [
      { clientId: "00000000-0000-4000-8000-00000000c102", name: "Marcus Hale", companyName: "The GroundsMen of New Orleans" },
    ],
    phoneEnd: "7731",
    lastSeenAt: agoIso(60 * 5),
    devices: [
      {
        id: "00000000-0000-4000-8000-00000000a102",
        device: "Chrome on Windows",
        ip: "98.4.11.203",
        signedInAt: agoIso(60 * 5),
        lastSeenAt: agoIso(60 * 5),
        lookOnly: true,
      },
      {
        id: "00000000-0000-4000-8000-00000000a103",
        device: "Chrome on Android",
        ip: "172.58.9.14",
        signedInAt: agoIso(60 * 24 * 9),
        lastSeenAt: agoIso(60 * 24 * 2),
        lookOnly: false,
      },
    ],
  },
];

interface Plan {
  status: "active" | "ended";
  feeCents: number;
  startedOn: string;
  card: { brand: string; last4: string } | null;
  addons: Record<string, { on: boolean; used: number }>;
  bills: BillRow[];
}
const plans = new Map<string, Plan>();

/** Seeds the preview's territories and the one franchise. Called once from the preview server. */
export function seedHq(franchiseId: string, zipsBy: Record<string, string[]>): void {
  for (const [k, v] of Object.entries(zipsBy)) zips.set(k, v);
  const bills: BillRow[] = [];
  let m = monthBefore(today());
  for (let i = 0; i < 6; i++) {
    const extra = i === 1 ? 1_350 : 0;
    bills.push({
      id: id(),
      month: m,
      feeCents: 25_000,
      addonsCents: 4_900,
      extraCents: extra,
      totalCents: 29_900 + extra,
      status: "paid",
      tries: 1,
      failure: null,
      paidAt: `${m}-02T15:00:00.000Z`,
    });
    m = monthBefore(`${m}-01`);
  }
  plans.set(franchiseId, {
    status: "active",
    feeCents: 25_000,
    startedOn: `${m}-01`,
    card: { brand: "Visa", last4: "4242" },
    addons: { eunice: { on: true, used: 212 }, cold_email: { on: false, used: 0 }, finder: { on: false, used: 0 } },
    bills,
  });
}

/** An owner from above working in a franchise that keeps its customer names private. */
export function namesPrivate(companyId: string, viaAbove: boolean): boolean {
  return viaAbove && hidden.has(companyId);
}

/** Turned off: no sign in anywhere. */
export const loginOff = (userId: string) => disabled.has(userId);

// Answers -------------------------------------------------------------------------------------------------------------

function planOf(ctx: HqContext, co: HqCo): PlanAnswer {
  const p = plans.get(co.id);
  const owner = ctx.users.find((u) => u.roles[co.id] === "franchise_owner");
  const addons: AddonRow[] = ADDONS.map((a) => ({
    addon: a,
    on: p?.addons[a]?.on ?? false,
    ...ADDON_DEFAULTS[a],
    usedThisMonth: p?.addons[a]?.used ?? 0,
  }));
  return {
    companyId: co.id,
    name: co.name,
    status: p ? p.status : "none",
    feeCents: p?.feeCents ?? 25_000,
    startedOn: p?.startedOn ?? null,
    hqSeesNames: !hidden.has(co.id),
    ownerName: owner?.name ?? null,
    card: p?.card ? { ...p.card, bank: false } : null,
    addonsPaused: false,
    addons,
    bills: p?.bills ?? [],
  };
}

const seed = (s: string) => s.length * 7 + s.charCodeAt(0) + s.charCodeAt(s.length - 1);

function overviewRow(ctx: HqContext, co: HqCo): OverviewRow {
  const level = ctx.levelOf(co);
  const k = seed(co.name) % 17;
  const kids = ctx.below(co.id).filter((x) => ctx.levelOf(x) === "location");
  const own = level === "location" ? 1 : 0;
  const scale = kids.length + own;
  const p = plans.get(co.id);
  return {
    companyId: co.id,
    name: co.name,
    brandColor: null,
    // Made up: one sub location (a real branch) under the New Orleans cleaners, so the tree shows its third step.
    subLocations:
      co.name === "The Cleaners of New Orleans"
        ? [
            {
              name: "Kenner",
              fullName: "The Cleaners of Kenner",
              phone: "+15045550190",
              zips: ["70062", "70065"],
              email: "kenner@example.com",
              address: { street1: "2100 Williams Blvd", city: "Kenner", state: "LA", zip: "70062" },
            },
          ]
        : [],
    parentId: co.parentId,
    level,
    status: co.status,
    runBy: p?.status === "active" ? "franchise" : "hq",
    ownerName: ctx.users.find((u) => u.roles[co.id] === "franchise_owner")?.name ?? null,
    zips: zips.get(co.id)?.length ?? 0,
    billStatus: p?.status === "active" ? (p.bills[0]?.status === "processing" ? "open" : (p.bills[0]?.status ?? null)) : null,
    namesPrivate: hidden.has(co.id),
    collected30Cents: scale * (2_140_000 + k * 31_000),
    collected12Cents: scale * (24_600_000 + k * 380_000),
    customersServed: scale * (148 + k),
    repeatingClients: scale * (92 + k),
    newThisMonth: scale * (9 + (k % 5)),
    repeatingMonthlyCents: scale * (1_480_000 + k * 22_000),
  };
}

function loginRows(ctx: HqContext, reach: Set<string>): SecurityLogin[] {
  return ctx.users
    .filter((u) => !disabled.has(u.id) && Object.keys(u.roles).some((cid) => reach.has(cid)))
    .map((u, i) => {
      const roles = Object.entries(u.roles)
        .filter(([cid]) => reach.has(cid))
        .map(([cid, role]) => ({ companyName: ctx.companies.find((x) => x.id === cid)?.name ?? "", role }));
      const all = Object.values(u.roles);
      const group: SecurityLogin["group"] = all.some((r) => r === "owner" || r === "franchise_owner")
        ? "owners"
        : all.some((r) => r === "manager" || r === "dispatch")
          ? "office"
          : "crew";
      const needs = all.some((r) => TWO_STEP_ROLES.includes(r));
      const mins = u.id === ctx.me.id ? 0 : 60 * (2 + i * 7);
      const phone = !u.email;
      return {
        userId: u.id,
        name: u.name,
        group,
        roles,
        twoStep: u.totp || (needs && u.id !== ctx.me.id) ? "app" : needs ? "not_set_up" : "not_asked",
        lastActiveAt: minsAgo(mins),
        idleDays: Math.floor(mins / 1440),
        devices: [
          {
            id: `${u.id}-d1`,
            kind: phone ? "mobile" : "web",
            device: phone ? "The phone app on Android" : "Chrome on Windows",
            ip: `10.0.0.${String(20 + i)}`,
            signedInAt: minsAgo(mins + 60 * 24 * 3),
            lastUsedAt: minsAgo(mins),
            twoStepPassed: !phone,
          },
        ],
        canAct: u.id !== ctx.me.id && roles.every((r) => canGiveRole(ctx.role, r.role)),
      };
    });
}

// Routes --------------------------------------------------------------------------------------------------------------

export function hqRoute(ctx: HqContext, method: string, p: string, q: URLSearchParams, b: Json): Answer | null {
  const { c } = ctx;
  const reachList = [c, ...ctx.below(c.id)];
  const reach = new Set(reachList.map((x) => x.id));
  const find = (cid: string | undefined) => reachList.find((x) => x.id === cid);
  const parts = p.split("/");
  const route = `${method} ${p}`;

  if (p.startsWith("hq/") && !ctx.gate(method === "GET" || p === "hq/place" ? "hq.read" : "hq.update")) return NO();

  if (route === "GET hq/overview") {
    const rows = ctx.below(c.id).map((x) => overviewRow(ctx, x));
    const locs = rows.filter((r) => r.level === "location");
    const sum = (
      k:
        | "collected30Cents"
        | "collected12Cents"
        | "customersServed"
        | "repeatingClients"
        | "newThisMonth"
        | "repeatingMonthlyCents",
    ) => locs.reduce((a, r) => a + r[k], 0);
    const months = [];
    for (let i = 11, m = today().slice(0, 7); i >= 0; i--) {
      const byCompany = Object.fromEntries(
        locs.map((r) => [r.companyId, Math.round(r.collected30Cents * (0.78 + ((i * 7) % 5) / 20))]),
      );
      const collectedCents = Object.values(byCompany).reduce((a, v) => a + v, 0);
      months.unshift({ month: m, collectedCents, byCompany });
      m = monthBefore(`${m}-01`);
    }
    return ok({
      level: ctx.levelOf(c),
      totals: {
        collected30Cents: sum("collected30Cents"),
        collected12Cents: sum("collected12Cents"),
        customersServed: sum("customersServed"),
        repeatingClients: sum("repeatingClients"),
        newThisMonth: sum("newThisMonth"),
        repeatingMonthlyCents: sum("repeatingMonthlyCents"),
        locations: locs.length,
        franchises: rows.filter((r) => r.runBy === "franchise").length,
        billsFailed: rows.filter((r) => r.billStatus === "failed").length,
        reviewOverdue: !review,
      },
      months,
      rows,
    });
  }

  if (route === "GET hq/territories")
    return ok({
      rows: reachList.map((x) => ({
        companyId: x.id,
        name: x.name,
        parentId: x.parentId,
        level: ctx.levelOf(x),
        franchise: plans.get(x.id)?.status === "active",
        zips: zips.get(x.id) ?? [],
        // The preview has no ZIP list, so every ZIP reads as New Orleans, about 15 miles out.
        areas: zips.get(x.id)?.length ? [{ city: "New Orleans", state: "LA", zips: zips.get(x.id) ?? [] }] : [],
        radiusMiles: zips.get(x.id)?.length ? 15 : null,
      })),
    });
  // The preview saves nothing, so sub locations come back as sent.
  if (method === "PUT" && parts[0] === "hq" && parts[1] === "sub-locations")
    return ok({ lines: (b as { lines?: unknown }).lines ?? [] });
  if (method === "PUT" && parts[0] === "hq" && parts[1] === "territories") {
    const target = find(parts[2]);
    if (!target) return fail(404, "not_found", "That company was not found.");
    if (ctx.levelOf(target) === "hq") return fail(400, "hq_no_zips", "Head office has no ZIP codes. Set them on a location.");
    const list = cleanZips((b.zips as string[] | undefined) ?? []).zips;
    const brand = [target, ...ctx.companies].find(
      (x) => ctx.levelOf(x) === "brand" && (x.id === target.id || x.id === target.parentId),
    );
    const others = brand ? [brand, ...ctx.below(brand.id)].filter((x) => x.id !== target.id) : [];
    for (const o of others) {
      const hit = (zips.get(o.id) ?? []).find((z) => list.includes(z));
      if (hit) return fail(409, "zip_taken", `${hit} already belongs to ${o.name}. A ZIP can have only one location.`);
    }
    zips.set(target.id, list);
    ctx.log(target, "territories.update", { count: list.length });
    return ok({ zips: list });
  }
  if (route === "POST hq/place") {
    const zip = zipOf(b.zip as string | undefined, b.address as string | undefined);
    const deepest = [...reachList].reverse().find((x) => zip && (zips.get(x.id) ?? []).includes(zip));
    if (deepest) return ok({ companyId: deepest.id, name: deepest.name, how: "zip", outside: false });
    const home = reachList.find((x) => ctx.levelOf(x) === "location" && !plans.has(x.id) && !zips.get(x.id)?.length) ?? c;
    return ok({ companyId: home.id, name: home.name, how: "home", outside: zip !== null });
  }

  if (route === "GET hq/people") {
    const rows: HqPersonRow[] = ctx.users
      .filter((u) => Object.keys(u.roles).some((cid) => reach.has(cid)) || ended.has(u.id))
      .map((u): HqPersonRow => ({
        userId: u.id,
        name: u.name,
        email: u.email,
        phone: u.phone,
        disabled: disabled.has(u.id),
        twoStep: u.totp ? "app" : Object.values(u.roles).some((r) => TWO_STEP_ROLES.includes(r)) ? "app" : "not_asked",
        lastActiveAt: minsAgo(90 + u.name.length * 37),
        memberships: [
          ...Object.entries(u.roles)
            .filter(([cid]) => reach.has(cid))
            .map(([cid, role]) => ({ companyId: cid, companyName: find(cid)?.name ?? "", role, active: true })),
          ...(ended.has(u.id) && find(ended.get(u.id))
            ? [{ companyId: ended.get(u.id)!, companyName: find(ended.get(u.id))!.name, role: "crew" as Role, active: false }]
            : []),
        ],
      }))
      .sort((a, z) => a.name.localeCompare(z.name));
    return ok(rows);
  }
  if (parts[0] === "hq" && parts[1] === "people" && parts[2]) {
    const u = ctx.users.find((x) => x.id === parts[2]);
    if (!u) return fail(404, "not_found", "That person was not found.");
    if (u.id === ctx.me.id) return fail(403, "not_yourself", "You can't change your own access here.");
    if (Object.entries(u.roles).some(([cid, r]) => reach.has(cid) && !canGiveRole(ctx.role, r)))
      return fail(403, "not_below_you", "You can't change someone at your level or above.");
    if (parts[3] === "roles") {
      const target = find(parts[4]);
      if (!target) return fail(404, "not_found", "That company was not found.");
      if (method === "DELETE") {
        Reflect.deleteProperty(u.roles, target.id);
        ended.set(u.id, target.id);
        ctx.log(c, "people.role_end");
        return ok({ ok: true });
      }
      const role = b.role as Role;
      if (!canGiveRole(ctx.role, role)) return fail(403, "role_too_high", "You can only give roles below your own.");
      if (role === "franchise_owner" && ctx.levelOf(target) !== "location")
        return fail(400, "franchise_owner_location", "A franchise owner works at one location. Pick a location.");
      u.roles[target.id] = role;
      ctx.log(c, "people.role", { role });
      return ok({ ok: true });
    }
    if (parts[3] === "off" || parts[3] === "on") {
      if (parts[3] === "off" && Object.keys(u.roles).some((cid) => !reach.has(cid)))
        return fail(
          409,
          "works_elsewhere",
          "This person also works for a company you don't run. Take away their role here instead.",
        );
      if (parts[3] === "off") disabled.add(u.id);
      else disabled.delete(u.id);
      ctx.log(c, parts[3] === "off" ? "people.login_off" : "people.login_on");
      return ok({ ok: true });
    }
  }

  if (route === "GET hq/franchises")
    return ok(
      reachList
        .filter((x) => ctx.levelOf(x) === "location")
        .map((x) => planOf(ctx, x))
        .sort((a, z) => a.name.localeCompare(z.name)),
    );
  if (parts[0] === "hq" && parts[1] === "franchises" && parts[2]) {
    const target = find(parts[2]);
    if (!target || ctx.levelOf(target) !== "location") return fail(404, "not_found", "That location was not found.");
    if (parts[3] === "charge") return ok(planOf(ctx, target));
    let plan = plans.get(target.id);
    if (!plan) {
      plan = { status: "active", feeCents: 25_000, startedOn: today(), card: null, addons: {}, bills: [] };
      plans.set(target.id, plan);
    }
    if (b.status === "active" || b.status === "ended") plan.status = b.status;
    if (typeof b.feeCents === "number") plan.feeCents = b.feeCents;
    if (typeof b.hqSeesNames === "boolean") {
      if (b.hqSeesNames) hidden.delete(target.id);
      else hidden.add(target.id);
    }
    for (const a of (b.addons as { addon: string; on: boolean }[] | undefined) ?? [])
      plan.addons[a.addon] = { on: a.on, used: plan.addons[a.addon]?.used ?? 0 };
    ctx.log(target, "franchise.plan");
    return ok(planOf(ctx, target));
  }

  // The security center and a franchise's own plan.
  if (p === "security" || p.startsWith("security/")) {
    if (!ctx.gate(method === "GET" ? "security.read" : "security.update")) return NO();
    if (route === "GET security")
      return ok({
        review: review
          ? {
              lastAt: review.at,
              byName: review.by,
              dueOn: new Date(Date.parse(review.at) + 90 * 86_400_000).toISOString().slice(0, 10),
              overdue: false,
            }
          : { lastAt: null, byName: null, dueOn: today(), overdue: true },
        logins: loginRows(ctx, reach),
      });
    if (route === "POST security/review") {
      review = { at: new Date().toISOString(), by: ctx.me.name };
      ctx.log(c, "security.reviewed");
      const dueOn = new Date(Date.now() + 90 * 86_400_000).toISOString().slice(0, 10);
      return ok({ lastAt: review.at, byName: review.by, dueOn, overdue: false });
    }
    if (route === "GET security/activity") {
      const only = q.get("companyId");
      const rows: ActivityRow[] = ctx.audit
        .filter((a) => reach.has(a.companyId) && (!only || a.companyId === only))
        .slice(0, 200)
        .map((a) => ({
          id: a.id,
          at: a.createdAt,
          companyId: a.companyId,
          companyName: find(a.companyId)?.name ?? "",
          userId: a.userId,
          userName: a.userName,
          action: a.action,
          subjectType: a.subjectType,
          subjectId: a.subjectId,
          ip: a.ip,
          details: a.companyId !== c.id && hidden.has(a.companyId) ? {} : a.details,
        }));
      return ok(rows);
    }
    if (route === "POST security/sign-out-everyone") {
      const mine = loginRows(ctx, reach).filter((l) => l.canAct);
      ctx.log(c, "security.sign_out_all");
      return ok({ logins: mine.length, devices: mine.reduce((n, l) => n + l.devices.length, 0) });
    }
    if (parts[1] === "devices" && parts[2]) {
      const row = loginRows(ctx, reach).find((l) => l.devices.some((d) => d.id === parts[2]));
      if (!row) return fail(404, "not_found", "That device was not found.");
      if (!row.canAct) return fail(403, "not_below_you", "You can't do this to yourself or to someone at your level or above.");
      ctx.log(c, "security.sign_out_device");
      return ok({ ok: true });
    }
    if (route === "GET security/hub") return ok(hubRows);
    if (route === "POST security/hub/sign-out") {
      const v = b as { sessionId: string; all?: boolean };
      const row = hubRows.find((h) => h.devices.some((d) => d.id === v.sessionId));
      if (!row) return fail(404, "not_found", "That sign-in was not found.");
      const gone = v.all ? row.devices.length : 1;
      row.devices = v.all ? [] : row.devices.filter((d) => d.id !== v.sessionId);
      hubRows = hubRows.filter((h) => h.devices.length > 0);
      ctx.log(c, "security.hub_sign_out");
      return ok({ devices: gone });
    }
    if (parts[1] === "logins" && parts[2]) {
      const row = loginRows(ctx, reach).find((l) => l.userId === parts[2]);
      if (!row) return fail(404, "not_found", "That login was not found.");
      if (!row.canAct) return fail(403, "not_below_you", "You can't do this to yourself or to someone at your level or above.");
      if (parts[3] === "sign-out") {
        ctx.log(c, "security.sign_out");
        return ok({ devices: row.devices.length });
      }
      const u = ctx.users.find((x) => x.id === parts[2])!;
      const gone = Object.keys(u.roles).filter((cid) => reach.has(cid));
      for (const cid of gone) Reflect.deleteProperty(u.roles, cid);
      ctx.log(c, "security.remove_access");
      return ok({ companies: gone.length });
    }
  }
  if (p === "franchise" || p.startsWith("franchise/")) {
    if (!ctx.gate(method === "GET" ? "franchise.read" : "franchise.update")) return NO();
    if (route === "GET franchise") return ok(planOf(ctx, c));
    const plan = plans.get(c.id);
    if (!plan) return fail(409, "not_franchise", "Only a franchise pays The Trades a fee.");
    if (route === "POST franchise/card")
      return ok({ setupId: "seti_preview", clientSecret: "seti_preview_secret", publishableKey: "pk_test_preview", fake: true });
    if (route === "POST franchise/card/confirm") {
      plan.card = { brand: "Visa", last4: "4242" };
      ctx.log(c, "franchise.card");
      return ok(planOf(ctx, c));
    }
  }
  return null;
}
